// Mapping delle prenotazioni CSV spiagge.it → celle Kalé Cora.
// Estratto da functions/scripts/import-spiagge-it.mjs per riuso lato admin.
//
// Layout Kalé Cora (le formule sono legate alle coordinate del beach builder):
//   Row 0 → 1° Fila mare (cols 2-7 e 11-18)
//   Row 1 → 2° Fila       (cols 0-7, 8, 10, 11-19)
//   Row 2 → 3° Fila       (cols 1-7, 8, 10, 11-19)
//   Gazebo  → B9, B11, C9, C11 (cols 8/10 di row 1/2)
//   Area Solarium → A21, A22, B21, B22 (cols 20/21 di row 0/1, 15 posti ciascuna)

export const TYPE_OMBRELLONE = 's1776059259879';
export const TYPE_GAZEBO = 's1778878412074';
export const TYPE_AREA_LETTINI = 's1776058850700';
export const LETTERS = 'ABCDEF';

// ─── CSV parsing ────────────────────────────────────────────────────────────

function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQ = false;
      } else cur += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === ';') { out.push(cur); cur = ''; }
      else cur += c;
    }
  }
  out.push(cur);
  return out;
}

export function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  let start = 0;
  if (lines[0]?.toLowerCase().startsWith('sep=')) start = 1;
  const header = parseCsvLine(lines[start]).map(h => h.trim());
  const rows = [];
  for (let i = start + 1; i < lines.length; i++) {
    const fields = parseCsvLine(lines[i]);
    const row = {};
    header.forEach((h, j) => { row[h] = (fields[j] ?? '').trim(); });
    rows.push(row);
  }
  return rows;
}

// ─── Mapping ────────────────────────────────────────────────────────────────

export function settoreId(str) {
  const m = /Id:\s*(\d+)/i.exec(str || '');
  return m ? Number(m[1]) : null;
}

export function cellId(row, col) {
  return `${LETTERS[row]}${col + 1}`;
}

// Ritorna { row, col, id, type, isArea, servizioLabel? } o { skip: reason }
export function mapPostazione(settore, numero, tipologia) {
  const sId = settoreId(settore);
  const num = Number(numero);

  const isGazebo = sId === 4 || (tipologia || '').toLowerCase() === 'gazebo';

  if (isGazebo) {
    const gazebo = { 1: [1, 8], 2: [1, 10], 3: [2, 8], 4: [2, 10] };
    if (!gazebo[num]) return { skip: `Gazebo #${num} non esiste in Kalé Cora` };
    const [row, col] = gazebo[num];
    return { row, col, id: cellId(row, col), type: TYPE_GAZEBO, isArea: false };
  }

  if (sId === 1) {
    // 1° Fila (row 0). Blocco dx: num 2-9 → col = 20-num. Blocco sx: num 10-15 → col = 17-num.
    if (num >= 2 && num <= 9) {
      const col = 20 - num;
      return { row: 0, col, id: cellId(0, col), type: TYPE_OMBRELLONE, isArea: false };
    }
    if (num >= 10 && num <= 15) {
      const col = 17 - num;
      return { row: 0, col, id: cellId(0, col), type: TYPE_OMBRELLONE, isArea: false };
    }
    return { skip: `1° Fila num ${num} fuori range 2-15` };
  }

  if (sId === 2) {
    // 2° Fila (row 1). Blocco dx: num 16-24 → col = 35-num (cols 11-19),
    // blocco sx: num 25-32 → col = 32-num (cols 0-7).
    if (num >= 16 && num <= 24) {
      const col = 35 - num;
      return { row: 1, col, id: cellId(1, col), type: TYPE_OMBRELLONE, isArea: false };
    }
    if (num >= 25 && num <= 32) {
      const col = 32 - num;
      return { row: 1, col, id: cellId(1, col), type: TYPE_OMBRELLONE, isArea: false };
    }
    return { skip: `2° Fila num ${num} fuori range 16-32 (posto non presente nel layout KC)`, missingInLayout: true };
  }

  if (sId === 3) {
    // 3° Fila su row 2. Blocco dx 33-41 → col = 52-num (cols 11-19),
    // blocco sx 42-48 → col = 49-num (cols 1-7).
    if (num >= 33 && num <= 41) {
      const col = 52 - num;
      return { row: 2, col, id: cellId(2, col), type: TYPE_OMBRELLONE, isArea: false };
    }
    if (num >= 42 && num <= 48) {
      const col = 49 - num;
      return { row: 2, col, id: cellId(2, col), type: TYPE_OMBRELLONE, isArea: false };
    }
    // Numeri > 48 di 3° Fila stanno fisicamente sulla 4° fila di spiagge.it,
    // che non ha una controparte nel layout Kalé Cora (la row 3 KC è la fila
    // "backup" del lido, sempre bloccata). Nessun rischio doppia prenotazione
    // perché quei posti non sono comunque prenotabili dall'app KC.
    if (num >= 49) {
      return { skip: `3° Fila num ${num}: posto solo su spiagge.it, non nel layout KC`, missingInLayout: true };
    }
    return { skip: `3° Fila num ${num} fuori range 2-48` };
  }

  if (sId === 6) {
    // 3° Fila Convenzione: posti solo su spiagge.it, non nel layout KC (backup fila).
    return { skip: `3° Fila Convenzione num ${num}: posto solo su spiagge.it, non nel layout KC`, missingInLayout: true };
  }

  if (sId === 7) {
    // 4° Fila Convenzione: posti solo su spiagge.it, non nel layout KC (backup fila).
    return { skip: `4° Fila Convenzione num ${num}: posto solo su spiagge.it, non nel layout KC`, missingInLayout: true };
  }

  if (sId === 5) {
    // Area Solarium: singola area unificata A22 (row 0 col 21) con capacitaMax 60.
    // Prima erano 4 aree separate (A22, A23, B22, B23) da 15 posti l'una, ma
    // condividevano lo stesso contatore per servizioId → bug di doppie prenotazioni.
    if (num < 1 || num > 60) return { skip: `Solarium lettino ${num} fuori range 1-60` };
    return {
      row: 0, col: 21, id: cellId(0, 21),
      type: TYPE_AREA_LETTINI,
      isArea: true,
      servizioLabel: 'Area lettini',
    };
  }

  return { skip: `Settore non riconosciuto: "${settore}" (id ${sId})` };
}

// ─── Date parsing ───────────────────────────────────────────────────────────

// "10-07-2026" → "2026-07-10"
export function toIsoDate(dmy) {
  const [d, m, y] = dmy.split('-');
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

export function expandRange(dalIso, alIso) {
  const out = [];
  const [dy, dm, dd] = dalIso.split('-').map(Number);
  const [ay, am, ad] = alIso.split('-').map(Number);
  let y = dy, mo = dm, d = dd;
  while (y < ay || (y === ay && mo < am) || (y === ay && mo === am && d <= ad)) {
    out.push(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    d++;
    const dim = new Date(y, mo, 0).getDate();
    if (d > dim) { d = 1; mo++; }
    if (mo > 12) { mo = 1; y++; }
  }
  return out;
}

// ─── Prezzo ─────────────────────────────────────────────────────────────────

// "310,00" → 310  |  "43,40" → 43.4
export function parsePrice(str) {
  if (!str) return 0;
  return Number(String(str).replace(/\./g, '').replace(',', '.')) || 0;
}

// ─── Riga CSV → piano ──────────────────────────────────────────────────────

// Trasforma una riga CSV in un "plan" pronto per Firestore.
// Ritorna { identificativo, skip: null | reason, order?, dates?, mapped? }
export function planRow(row) {
  const identificativo = row['Identificativo'];
  const settore = row['Settore'];
  const tipologia = row['Tipologia Postazione'];
  const numero = row['Numero Postazione'];
  const nome = row['Nome'] || '';
  const cognome = row['Cognome'] || '';
  const email = row['Email'] || '';
  const telefono = row['Telefono'] || '';
  const allestimento = row['Allestimento'] || '';
  const canale = row['Canale'] || '';
  const dalRaw = row['Data inizio'];
  const alRaw = row['Data Fine'];
  const prezzo = parsePrice(row['Prezzo Totale Applicato']);
  const dataCancellazione = (row['Data Cancellazione'] || '').trim();

  // Prenotazioni cancellate su spiagge.it: skip categorizzato — il cliente
  // ha annullato, non dobbiamo tenerle bloccate in KC.
  if (dataCancellazione) {
    return {
      identificativo,
      skip: `cancellata su spiagge.it il ${dataCancellazione}`,
      skipCategory: 'cancelled-on-spiagge',
    };
  }

  const mapped = mapPostazione(settore, numero, tipologia);
  if (mapped.skip) {
    return {
      identificativo,
      skip: mapped.skip,
      skipCategory: mapped.missingInLayout ? 'missing-layout' : 'error',
    };
  }

  const dal = toIsoDate(dalRaw);
  const al = toIsoDate(alRaw);
  const dates = expandRange(dal, al);

  const guestName = `${nome} ${cognome}`.trim();
  const notesParts = [
    `spiagge.it #${identificativo}`,
    tipologia,
    allestimento && `all. ${allestimento}`,
    canale && `canale: ${canale}`,
  ].filter(Boolean);

  const cella = {
    id: mapped.id,
    row: mapped.row,
    col: mapped.col,
    type: mapped.type,
    status: 'free',
  };
  if (mapped.isArea) cella.quantita = 1;

  const isRange = dates.length > 1;
  return {
    identificativo,
    skip: null,
    mapped,
    dates,
    order: {
      celle: [cella],
      guestName,
      guestPhone: telefono,
      guestEmail: email,
      notes: notesParts.join(' · '),
      // Attenzione: expandDates() nel service per 'single' fa `return [selectedDate]`,
      // quindi selectedDate deve essere una STRINGA (non un array), altrimenti
      // finisce come nested array e Firestore rifiuta la scrittura.
      dateType: isRange ? 'range' : 'single',
      selectedDate: isRange ? undefined : dates[0],
      dateRange: isRange ? { dal, al } : null,
      seasonalDays: null,
      totalPrice: prezzo,
      status: 'blocked',
      isArea: mapped.isArea,
      servizioId: mapped.isArea ? mapped.type : null,
      servizioLabel: mapped.isArea ? mapped.servizioLabel : null,
    },
  };
}
